import React from 'react';
import dayjs from 'dayjs';
import { I18n } from '@aws-amplify/core';
import { Tooltip } from 'antd';

import { DownloadIcon, TrashIcon } from '@/assets/icons/v2';
import { dateFormat } from '@/utils/date-format';
import { ACTION_ICON_SIZE, ACTIONS_SPAN, DATE_SPAN, TLID_SPAN, VERSION_SPAN } from '@/views/Bera/constants';

import type { BeraVersion } from '@/types/Bera';
import * as S from './styles';

interface VersionItemProps {
	label: string;
	latest: boolean;
	current: boolean;
	version: BeraVersion;
	onShowTlids: (version: BeraVersion, label: string) => void;
	onDownload: (version: BeraVersion) => void;
	onDelete: (version: BeraVersion) => void;
}

export function VersionItem({
	label,
	latest,
	current,
	version,
	onShowTlids,
	onDownload,
	onDelete
}: Readonly<VersionItemProps>): JSX.Element {
	const isDeleteDisabled = !latest;
	const deleteTooltip = latest ? 'Delete' : 'Only the latest version can be deleted';

	function handleDownload() {
		onDownload(version);
	}

	function handleDelete() {
		onDelete(version);
	}

	function handleShowTlids() {
		onShowTlids(version, label);
	}

	return (
		<S.VersionRow align="middle" $current={current} aria-current={current}>
			<S.Cell span={VERSION_SPAN}>
				<S.VersionName>{label}</S.VersionName>
			</S.Cell>
			<S.Cell span={TLID_SPAN}>
				<S.TlidLink type="link" onClick={handleShowTlids}>
					{version.tlid_count}
				</S.TlidLink>
			</S.Cell>
			<S.Cell span={DATE_SPAN}>
				<S.VersionDate>{dayjs(version.created_at).format(dateFormat())}</S.VersionDate>
			</S.Cell>
			<S.ActionsCell span={ACTIONS_SPAN}>
				<Tooltip title={I18n.get('Download')}>
					<S.DownloadButton
						type="link"
						onClick={handleDownload}
						aria-label={I18n.get('Download')}
						icon={<DownloadIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />}
					/>
				</Tooltip>
				<Tooltip title={I18n.get(deleteTooltip)}>
					<S.DangerButton
						type="link"
						onClick={handleDelete}
						disabled={isDeleteDisabled}
						aria-label={I18n.get('Delete')}
						icon={<TrashIcon width={ACTION_ICON_SIZE} height={ACTION_ICON_SIZE} />}
					/>
				</Tooltip>
			</S.ActionsCell>
		</S.VersionRow>
	);
}
